import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Storefront, List, X } from "@phosphor-icons/react";
import AutocompleteSearch from "./AutocompleteSearch";
import ScarcityBadge from "./ScarcityBadge";
import AdminShield from "./AdminShield";

const LANGS = [
  { code: "fr", label: "FR" },
  { code: "en", label: "EN" },
  { code: "de", label: "DE" },
];

export default function Header() {
  const { lang, setLang, t } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const navLink = "text-sm font-medium text-foreground/70 hover:text-foreground transition-colors";
  const activeLink = "text-foreground";

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center gap-6">
        <AdminShield />
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="brand-home-link">
          <img src="/logo-mc68.png" alt="Mon Commerce 68" className="w-10 h-10 rounded-sm object-contain" />
          <div className="leading-tight hidden sm:block">
            <div className="font-display text-base font-semibold tracking-tight">Mon Commerce 68</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Haut-Rhin</div>
          </div>
        </Link>

        <div className="hidden lg:block flex-1 max-w-md" data-testid="header-autocomplete-wrap">
          <AutocompleteSearch compact placeholder={t.nav.search_placeholder} fallbackPath="/annuaire" />
        </div>

        <nav className="hidden md:flex items-center gap-6 ml-auto lg:ml-0">
          <NavLink to="/annuaire" className={({ isActive }) => `${navLink} ${isActive ? activeLink : ""}`} data-testid="nav-directory">
            {t.nav.directory}
          </NavLink>
          <NavLink to="/pages-blanches" className={({ isActive }) => `${navLink} ${isActive ? activeLink : ""}`} data-testid="nav-whitepages">
            Pages Blanches
          </NavLink>
          <NavLink to="/pourquoi-partenaire" className={({ isActive }) => `${navLink} ${isActive ? activeLink : ""}`} data-testid="nav-partner">
            {t.nav.partner}
          </NavLink>
          {user && (
            <NavLink to="/dashboard" className={({ isActive }) => `${navLink} ${isActive ? activeLink : ""}`} data-testid="nav-dashboard">
              {t.nav.dashboard}
            </NavLink>
          )}
          {user?.role === "admin" && (
            <NavLink to="/admin" className={({ isActive }) => `${navLink} ${isActive ? activeLink : ""}`} data-testid="nav-admin">
              {t.nav.admin}
            </NavLink>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3 ml-auto lg:ml-0">
          <ScarcityBadge />
          <div className="flex items-center border border-border rounded-sm overflow-hidden text-xs" data-testid="lang-switcher">
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                className={`px-2.5 py-1 font-semibold tracking-wider transition-colors ${
                  lang === l.code ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`lang-${l.code}`}
              >
                {l.label}
              </button>
            ))}
          </div>

          {user ? (
            <button
              onClick={() => { logout(); navigate("/"); }}
              className="text-sm font-semibold px-4 py-2 border border-border rounded-sm hover:border-foreground transition-colors"
              data-testid="logout-btn"
            >
              {t.nav.logout}
            </button>
          ) : (
            <Link
              to="/connexion"
              className="text-sm font-semibold px-4 py-2 bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white transition-colors rounded-sm"
              data-testid="login-cta"
            >
              {t.nav.login}
            </Link>
          )}
        </div>

        <button onClick={() => setOpen(!open)} className="md:hidden p-2" data-testid="mobile-menu-toggle" aria-label="menu">
          {open ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background" data-testid="mobile-menu">
          <div className="flex flex-col p-6 gap-4">
            <AutocompleteSearch placeholder={t.nav.search_placeholder} fallbackPath="/annuaire" />
            <Link to="/annuaire" onClick={() => setOpen(false)} className="text-base font-medium" data-testid="mobile-nav-directory">{t.nav.directory}</Link>
            <Link to="/pages-blanches" onClick={() => setOpen(false)} className="text-base font-medium" data-testid="mobile-nav-whitepages">Pages Blanches</Link>
            <Link to="/pourquoi-partenaire" onClick={() => setOpen(false)} className="text-base font-medium" data-testid="mobile-nav-partner">{t.nav.partner}</Link>
            {user && <Link to="/dashboard" onClick={() => setOpen(false)} className="text-base font-medium">{t.nav.dashboard}</Link>}
            {user?.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-base font-medium">{t.nav.admin}</Link>}
            <div className="flex gap-2 mt-2">
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => setLang(l.code)} className={`px-3 py-1 border border-border rounded-sm text-xs font-bold ${lang === l.code ? "bg-primary text-primary-foreground" : ""}`}>
                  {l.label}
                </button>
              ))}
            </div>
            {!user ? (
              <Link to="/connexion" onClick={() => setOpen(false)} className="bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold rounded-sm text-center">{t.nav.login}</Link>
            ) : (
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="px-4 py-2 border border-border text-sm font-semibold rounded-sm">{t.nav.logout}</button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
